import { catalogRowDisplayName } from './assetPromotedFacilities';

function normalizeFacilityName(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[_-]+/g, ' ');
}

/** Facilities removed from the main create/edit picker (still valid on existing assets). */
export function isAssetCreateHiddenFacilityName(name: string): boolean {
  const key = normalizeFacilityName(name);
  if (!key) {
    return false;
  }
  if (key === 'parking' || key === 'car park') {
    return true;
  }
  if (key === 'air conditioning' || key === 'a/c' || key === 'ac') {
    return true;
  }
  if (key === 'dedicated it support' || key.includes('dedicated it')) {
    return true;
  }
  return false;
}

export const ASSET_CREATE_PUBLIC_TRANSPORT_LABEL = 'Close to public transportation';

export const TRANSPORTATION_FACILITY_ORDER = [
  'Handicap Accessible',
  ASSET_CREATE_PUBLIC_TRANSPORT_LABEL,
  'Free Parking',
  'Paid Parking',
  'Electric Car Charging',
  'Bicycle Storage',
] as const;

type TransportationKind =
  | 'handicap'
  | 'public_transport'
  | 'free_parking'
  | 'paid_parking'
  | 'ev'
  | 'bicycle';

const TRANSPORTATION_KIND_BY_CANONICAL_LABEL: Record<
  (typeof TRANSPORTATION_FACILITY_ORDER)[number],
  TransportationKind
> = {
  'Handicap Accessible': 'handicap',
  [ASSET_CREATE_PUBLIC_TRANSPORT_LABEL]: 'public_transport',
  'Free Parking': 'free_parking',
  'Paid Parking': 'paid_parking',
  'Electric Car Charging': 'ev',
  'Bicycle Storage': 'bicycle',
};

/** Match train before bus — both map to public transport. */
export function classifyTransportationFacility(name: string): TransportationKind | null {
  const key = normalizeFacilityName(name);
  if (!key) {
    return null;
  }
  if (/handicap|wheelchair accessible|accessible entrance|נגישות|נגיש ל/.test(key)) {
    return 'handicap';
  }
  if (
    /close to train|near train|train station|rail station|railway|רכבת|קרוב לרכבת|תחנת רכבת/.test(key)
  ) {
    return 'public_transport';
  }
  if (
    /close to bus|near bus|bus stop|אוטובוס|קרוב לאוטובוס|תחנת אוטובוס|קו אוטובוס/.test(key)
  ) {
    return 'public_transport';
  }
  if (/close to public transportation|public transportation|תחבורה ציבורית|קרוב לתחבורה/.test(key)) {
    return 'public_transport';
  }
  if (/free parking|חניה חינם/.test(key)) {
    return 'free_parking';
  }
  if (/paid parking|חניה בתשלום/.test(key)) {
    return 'paid_parking';
  }
  if (/electric car|ev charg|טעינת רכב|עמדת טעינה/.test(key)) {
    return 'ev';
  }
  if (/bicycle storage|bike storage|אחסון אופניים|חניה לאופניים/.test(key)) {
    return 'bicycle';
  }
  return null;
}

export function isPublicTransportationFacilityName(name: string): boolean {
  return classifyTransportationFacility(name) === 'public_transport';
}

export function isAssetCreateTransportationFacilityName(name: string): boolean {
  return classifyTransportationFacility(name) !== null;
}

export function isAssetCreateProtectSpaceFacilityName(name: string): boolean {
  const key = normalizeFacilityName(name);
  return /protected space|protect space|מרחב מוגן/.test(key);
}

/** Expected facility labels when the API catalog does not list them yet (no wire id). */
export const ASSET_CREATE_EXTRA_FACILITY_LABELS = [
  'Protected Space',
  ...TRANSPORTATION_FACILITY_ORDER,
] as const;

const EXTRA_FACILITY_ALIAS_KEYS: Record<string, readonly string[]> = {
  'Protected Space': ['protected space', 'protect space', 'מרחב מוגן'],
  'Handicap Accessible': ['handicap accessible', 'wheelchair accessible'],
  [ASSET_CREATE_PUBLIC_TRANSPORT_LABEL]: [
    'close to public transportation',
    'public transportation',
    'close to bus',
    'close to train',
    'near bus',
    'near train',
    'bus stop',
    'train station',
    'rail station',
    'תחבורה ציבורית',
    'קרוב לתחבורה',
    'קרוב לאוטובוס',
    'קרוב לרכבת',
  ],
  'Free Parking': ['free parking'],
  'Paid Parking': ['paid parking'],
  'Electric Car Charging': ['electric car charging', 'ev charging'],
  'Bicycle Storage': ['bicycle storage', 'bike storage'],
};

function catalogNameMatchesExtraLabel(catalogName: string, extraLabel: string): boolean {
  const normalizedCatalogName = normalizeFacilityName(catalogName);
  const normalizedExtraLabel = normalizeFacilityName(extraLabel);
  if (!normalizedCatalogName || !normalizedExtraLabel) {
    return false;
  }
  if (normalizedCatalogName === normalizedExtraLabel) {
    return true;
  }
  const aliases = EXTRA_FACILITY_ALIAS_KEYS[extraLabel] ?? [normalizedExtraLabel];
  return aliases.some((alias) => normalizeFacilityName(alias) === normalizedCatalogName);
}

export function enrichAssetCreateFacilityCatalog(
  catalog: Record<string, unknown>[],
): Record<string, unknown>[] {
  const out = [...catalog];
  const catalogNames = catalog
    .map((row) => normalizeFacilityName(catalogRowDisplayName(row)))
    .filter(Boolean);

  for (const label of ASSET_CREATE_EXTRA_FACILITY_LABELS) {
    const alreadyPresent = catalogNames.some((name) =>
      catalogNameMatchesExtraLabel(name, label),
    );
    if (!alreadyPresent) {
      out.push({ facility_name: label });
    }
  }
  return out;
}

export type AssetCreateFacilityCatalogPartition = {
  main: Record<string, unknown>[];
  transportation: Record<string, unknown>[];
};

/** Wire ids toggled together for one transportation checkbox (e.g. bus + train). */
export const LINKED_FACILITY_IDS_KEY = '__linkedFacilityIds';

function facilityRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.facility_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

export function linkedFacilityIdsForRow(row: Record<string, unknown>): number[] {
  const linked = row[LINKED_FACILITY_IDS_KEY];
  if (Array.isArray(linked)) {
    const ids = linked.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
    if (ids.length > 0) {
      return ids;
    }
  }
  const fid = facilityRowNumericId(row);
  return fid !== null ? [fid] : [];
}

function transportationSortRank(name: string): number {
  const kind = classifyTransportationFacility(name);
  if (!kind) {
    return TRANSPORTATION_FACILITY_ORDER.length + 1;
  }
  const canonical = TRANSPORTATION_FACILITY_ORDER.find(
    (label) => TRANSPORTATION_KIND_BY_CANONICAL_LABEL[label] === kind,
  );
  if (!canonical) {
    return TRANSPORTATION_FACILITY_ORDER.length;
  }
  return TRANSPORTATION_FACILITY_ORDER.indexOf(canonical);
}

function canonicalTransportationLabel(kind: TransportationKind): string {
  const canonical = TRANSPORTATION_FACILITY_ORDER.find(
    (label) => TRANSPORTATION_KIND_BY_CANONICAL_LABEL[label] === kind,
  );
  return canonical ?? '';
}

function collapseTransportationRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const byKind = new Map<TransportationKind, { row: Record<string, unknown>; ids: number[] }>();

  for (const row of rows) {
    const name = catalogRowDisplayName(row);
    const kind = classifyTransportationFacility(name);
    if (!kind) {
      continue;
    }
    const id = facilityRowNumericId(row);
    const existing = byKind.get(kind);
    if (existing) {
      if (id !== null && !existing.ids.includes(id)) {
        existing.ids.push(id);
      }
      continue;
    }
    byKind.set(kind, {
      row: {
        ...row,
        facility_name: canonicalTransportationLabel(kind) || name,
      },
      ids: id !== null ? [id] : [],
    });
  }

  const collapsed = Array.from(byKind.values()).map(({ row, ids }) => {
    if (ids.length <= 1) {
      return row;
    }
    return {
      ...row,
      [LINKED_FACILITY_IDS_KEY]: ids,
    };
  });

  collapsed.sort((a, b) => {
    const rankA = transportationSortRank(catalogRowDisplayName(a));
    const rankB = transportationSortRank(catalogRowDisplayName(b));
    if (rankA !== rankB) {
      return rankA - rankB;
    }
    return catalogRowDisplayName(a).localeCompare(catalogRowDisplayName(b));
  });

  return collapsed;
}

export function partitionAssetCreateFacilityCatalog(
  catalog: Record<string, unknown>[],
): AssetCreateFacilityCatalogPartition {
  const enriched = enrichAssetCreateFacilityCatalog(catalog);
  const main: Record<string, unknown>[] = [];
  const transportation: Record<string, unknown>[] = [];

  for (const row of enriched) {
    const name = catalogRowDisplayName(row);
    if (!name) {
      continue;
    }
    if (isAssetCreateHiddenFacilityName(name)) {
      continue;
    }
    if (isAssetCreateTransportationFacilityName(name)) {
      transportation.push(row);
      continue;
    }
    main.push(row);
  }

  return { main, transportation: collapseTransportationRows(transportation) };
}
